/**
 * Service-worker registration helper.
 *
 * We register *unconditionally* (not just in production) because the
 * preview environment runs `craco start`, and we still want Lighthouse
 * + the on-device "Add to Home Screen" prompt to find the SW there.
 * The SW itself is dev-aware — it whitelists CRA HMR paths so hot
 * reload keeps working.
 *
 * Update flow (see also `UpdateAvailableBanner.jsx`):
 *
 *   1. Browser auto-checks the SW every page load.
 *   2. If `service-worker.js` differs from the active one, the new
 *      copy installs but stays "waiting" (we removed the eager
 *      `self.skipWaiting()` from the SW for exactly this).
 *   3. Here we detect the waiting worker — either it was already
 *      there when the page loaded (rare, but happens if the user
 *      kept a tab open across the install), or it appears mid-session
 *      via an `updatefound` event — and dispatch a `torahya-sw-update-ready`
 *      window event with the registration so the banner can show.
 *   4. When the user taps "רענן", the banner posts `SKIP_WAITING`
 *      to the waiting worker; the SW activates; `controllerchange`
 *      fires; we reload the page once to pick up the fresh bundle.
 */

const UPDATE_EVENT = "torahya-sw-update-ready";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Service workers need a secure context. localhost counts as secure
  // for `navigator.serviceWorker.register`, so this is just a sanity
  // belt: in case someone proxies the app over plain HTTP we don't
  // crash the console with a SecurityError.
  if (!window.isSecureContext) return;

  // Defer until after `load` so SW registration never competes with
  // critical render work on the first paint.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((registration) => {
        wireUpdateDetection(registration);
      })
      .catch((err) => {
        // Non-fatal — the app works without a SW, we just lose
        // offline support.
        console.warn("[PWA] service-worker registration failed:", err);
      });

    // When the controller changes (i.e. the waiting SW just took over),
    // reload exactly once so the user sees the fresh bundle. We guard
    // against the infinite-reload trap with a sessionStorage flag.
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (hasReloaded) return;
      hasReloaded = true;
      window.location.reload();
    });
  });
}

function wireUpdateDetection(registration) {
  // (a) A waiting worker is already present (the page loaded into a
  // tab that had picked up the new SW on a previous visit).
  if (registration.waiting && navigator.serviceWorker.controller) {
    dispatchUpdate(registration.waiting);
  }

  // (b) New install observed during this session. Watch its state
  // transition: as soon as it reaches "installed" AND there's already
  // an active controller, that's a real update (not the first-ever
  // install).
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (
        installing.state === "installed" &&
        navigator.serviceWorker.controller
      ) {
        dispatchUpdate(installing);
      }
    });
  });
}

function dispatchUpdate(worker) {
  // The banner listens for this event. We pass the worker reference
  // in `detail` so it can post `SKIP_WAITING` to the right target.
  try {
    window.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, { detail: { worker } }),
    );
  } catch {
    /* CustomEvent unsupported on very old browsers — non-fatal. */
  }
}
